import React from "react";
import styled from "styled-components";

const SkeletonBox = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 8px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ProfileSkeletonLoader = () => {
  return (
    <div className="w-full flex flex-col gap-6 animate__animated animate__fadeIn">
      {/* 1. Header Navigation Skeleton */}
      <div className="w-full h-[82px] flex items-center justify-between px-6 md:px-10 bg-white rounded-[20px] shadow-sm">
        <div className="flex items-center gap-4">
          <SkeletonBox style={{ width: "24px", height: "24px", borderRadius: "50%" }} />
          <SkeletonBox style={{ width: "180px", height: "28px" }} />
        </div>
        <SkeletonBox style={{ width: "140px", height: "42px", borderRadius: "12px" }} />
      </div>

      <div className="flex flex-col gap-6">
        {/* 2. Top Identity Card Skeleton */}
        <div className="bg-white rounded-[24px] shadow-sm overflow-hidden border border-gray-50">
          <div className="h-[160px] w-full bg-gray-100">
            <SkeletonBox style={{ width: "100%", height: "100%", borderRadius: "0" }} />
          </div>
          <div className="px-6 md:px-10 relative">
            <div className="flex flex-col md:flex-row items-center gap-10 -mt-[80px]">
              <SkeletonBox 
                style={{ 
                  width: "140px", 
                  height: "140px", 
                  borderRadius: "50%", 
                  border: "6px solid white",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)" 
                }} 
              />
              <div className="flex-1 text-center md:text-left pt-12 md:pt-16 pb-8">
                <SkeletonBox style={{ width: "250px", height: "36px", marginBottom: "12px", margin: "0 auto md:0" }} />
                <div className="flex justify-center md:justify-start gap-3">
                  <SkeletonBox style={{ width: "120px", height: "24px", borderRadius: "8px" }} />
                  <SkeletonBox style={{ width: "120px", height: "24px", borderRadius: "8px" }} />
                </div>
              </div>
              <div className="pt-12 md:pt-16 pb-8">
                <SkeletonBox style={{ width: "160px", height: "48px", borderRadius: "12px" }} />
              </div>
            </div>
          </div>
        </div>

        {/* 3. Details Grid Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Details Skeleton */}
          <div className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-50">
            <div className="flex items-center gap-3 mb-6">
              <SkeletonBox style={{ width: "4px", height: "20px", borderRadius: "2px" }} />
              <SkeletonBox style={{ width: "150px", height: "20px" }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <SkeletonBox style={{ width: "100px", height: "14px", marginBottom: "8px" }} />
                <SkeletonBox style={{ width: "80%", height: "18px" }} />
              </div>
              <div>
                <SkeletonBox style={{ width: "100px", height: "14px", marginBottom: "8px" }} />
                <SkeletonBox style={{ width: "70%", height: "18px" }} />
              </div>
            </div>
          </div>

          {/* Location Details Skeleton */}
          <div className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-50">
            <div className="flex items-center gap-3 mb-6">
              <SkeletonBox style={{ width: "4px", height: "20px", borderRadius: "2px" }} />
              <SkeletonBox style={{ width: "150px", height: "20px" }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <SkeletonBox style={{ width: "100px", height: "14px", marginBottom: "8px" }} />
                <SkeletonBox style={{ width: "90%", height: "18px" }} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <SkeletonBox style={{ width: "60px", height: "14px", marginBottom: "8px" }} />
                  <SkeletonBox style={{ width: "100%", height: "18px" }} />
                </div>
                <div>
                  <SkeletonBox style={{ width: "60px", height: "14px", marginBottom: "8px" }} />
                  <SkeletonBox style={{ width: "100%", height: "18px" }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Bottom Section Skeleton */}
        <div className="w-full bg-white rounded-[24px] shadow-sm border border-gray-50 overflow-hidden">
          <div className="px-6 md:px-8 pt-6 pb-2 border-b border-gray-50 flex gap-4 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} style={{ width: "100px", height: "36px", borderRadius: "10px", flexShrink: 0 }} />
            ))}
          </div>
          <div className="p-8 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonBox key={i} style={{ height: "140px", borderRadius: "24px" }} />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8">
                <SkeletonBox style={{ height: "300px", borderRadius: "32px" }} />
              </div>
              <div className="lg:col-span-4">
                <SkeletonBox style={{ height: "300px", borderRadius: "32px" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSkeletonLoader;

